import React, { useEffect } from "react";
import type { ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useRouter } from "@/shared/lib/router/router";
import Head from "@/shared/lib/router/head";
import Layout from "@/widgets/app-shell/ui/layout";
import { AuthProvider } from "@/entities/session/model/AuthProvider";
import { isTauriRuntime } from "@/shared/lib/tauri";
import { CRMCallProvider } from "@/features/crm/model/CRMCallContext";
import { ToastProvider } from "@/shared/ui/toast/ToastContext";
import { TenantSessionSync } from "@/features/tenant-session/ui/TenantSessionSync";
import { NotificationProvider } from "@/features/notification-center/model/NotificationContext";
import { ProjectProvider } from "@/entities/project/model/projectContext";
import { StorageProvider } from "@/entities/storage/model/StorageContext";
import { TabProvider } from "@/shared/model/TabContext";
import { SidebarProvider } from "@/shared/model/SidebarContext";
import { ThemeProvider } from "@/shared/model/ThemeContext";
import { UserPreferencesProvider } from "@/entities/user/model/UserPreferencesContext";
import { queryClient } from "@/shared/lib/queryClient";
import { setLocale } from "@/shared/i18n";

type AppProvidersProps = {
  children: ReactNode;
  /** Página sem o chrome do shell (rail/topbar). */
  flush?: boolean;
  /** Classes de fonte do wrapper raiz (o Next injeta via next/font). */
  fontClassName?: string;
};

/** Árvore de providers + shell, comum ao Next (_app) e ao Vite (src/vite/main). */
export function AppProviders({ children, flush, fontClassName = "" }: AppProvidersProps) {
  const router = useRouter();

  // Mantém a instância standalone do i18next (schemas Zod, services) alinhada com o locale ativo
  useEffect(() => {
    if (router.locale) {
      setLocale(router.locale);
    }
  }, [router.locale]);

  useEffect(() => {
    document.body.classList.add("font-sans");
    if (!isTauriRuntime()) return;
    const handleContextMenu = (e: MouseEvent) => e.preventDefault();
    document.addEventListener("contextmenu", handleContextMenu);
    return () => document.removeEventListener("contextmenu", handleContextMenu);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <div className={`${fontClassName} font-sans`}>
        <AuthProvider>
          <ToastProvider>
            <TenantSessionSync />
            <CRMCallProvider>
              <UserPreferencesProvider>
                <NotificationProvider>
                  <ProjectProvider>
                    <StorageProvider>
                      <TabProvider>
                        <SidebarProvider>
                          <Head>
                            <title>7Days Digital</title>
                            <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
                          </Head>
                          <Layout flush={flush}>{children}</Layout>
                        </SidebarProvider>
                      </TabProvider>
                    </StorageProvider>
                  </ProjectProvider>
                </NotificationProvider>
              </UserPreferencesProvider>
            </CRMCallProvider>
          </ToastProvider>
        </AuthProvider>
      </div>
    </ThemeProvider>
    <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
